type JiraReportingConfig = {
  jql?: Record<string, string>;
  customFields?: Record<string, string>;
};

export type JiraLinkConfig = {
  baseUrl?: string;
  reporting?: JiraReportingConfig;
};

function normalizeBaseUrl(baseUrl: string): string {
  return String(baseUrl || '').replace(/\/+$/, '');
}

function toProjectKeysCsv(keys: string[]): string {
  return keys
    .map((k) => String(k || '').trim())
    .filter(Boolean)
    .join(', ');
}

function stripOrderBy(jql: string): string {
  return String(jql || '').replace(/\border\s+by\b[\s\S]*$/i, '').trim();
}

function priorityJqlField(customFieldId: string | undefined): string | undefined {
  const raw = String(customFieldId || '').trim();
  if (!raw) return undefined;
  const m = raw.match(/customfield_(\d+)/i);
  if (!m) return undefined;
  return `cf[${m[1]}]`;
}

function rewritePriorityFieldInJql(jql: string, priorityField: string | undefined): string {
  const src = String(jql || '');
  if (!priorityField) return src;
  return src.replace(/\bpriority\b/g, priorityField);
}

function rewriteTimeToResolutionFieldInJql(jql: string, slaField: string | undefined): string {
  const src = String(jql || '');
  if (!slaField) return src;
  return src.replace(/"Time to resolution"/gi, slaField);
}

function expandProjectKeyInJql(template: string, projectKey: string): string {
  const pk = String(projectKey || '').trim();
  const src = String(template || '');
  if (!pk) return src;
  return src
    .replace(/\{projectKey\}/g, pk)
    .replace(/\{PROJECT_KEY\}/g, pk)
    .replace(/\bPROJECT_KEY\b/g, pk);
}

function expandProjectKeysInJql(template: string, projectKeys: string[]): string {
  const keys = (projectKeys || []).map((k) => String(k || '').trim()).filter(Boolean);
  const src = String(template || '');
  if (keys.length === 0) return src;
  if (keys.length === 1) return expandProjectKeyInJql(src, keys[0]);

  // Multi-project: rewrite `project = {projectKey|PROJECT_KEY}` to `project in (A, B, ...)`.
  // This keeps the remainder of the template intact, matching how the backend aggregates per-tenant metrics.
  const inClause = `project in (${toProjectKeysCsv(keys)})`;
  // NOTE: do NOT use `\b` after `}`: both `}` and whitespace are non-word, so word-boundary won't match.
  // Use a lookahead for common terminators instead.
  const rewritten = src
    .replace(/\bproject\s*=\s*\{projectKey\}(?=\s|\)|$)/gi, inClause)
    .replace(/\bproject\s*=\s*\{PROJECT_KEY\}(?=\s|\)|$)/gi, inClause)
    .replace(/\bproject\s*=\s*PROJECT_KEY(?=\s|\)|$)/gi, inClause);

  // If the template didn't use a recognized placeholder form, fall back to single-key substitution.
  return expandProjectKeyInJql(rewritten, keys[0]);
}

export function jiraIssueUrl(baseUrl: string | undefined, issueKey: string | undefined | null): string | null {
  const b = normalizeBaseUrl(String(baseUrl || ''));
  const k = String(issueKey || '').trim();
  if (!b || !k) return null;
  return `${b}/browse/${encodeURIComponent(k)}`;
}

export function jiraListUrl(baseUrl: string | undefined, jql: string): string | null {
  const b = normalizeBaseUrl(String(baseUrl || ''));
  const q = String(jql || '').trim();
  if (!b || !q) return null;
  return `${b}/issues/?jql=${encodeURIComponent(q)}`;
}

export function jiraJqlFromTemplate(args: {
  jiraLinkConfig: JiraLinkConfig | null | undefined;
  templateKey: string;
  projectKey?: string;
  projectKeys?: string[];
  stripOrderBy?: boolean;
}): string | null {
  const cfg = args.jiraLinkConfig;
  const raw = cfg?.reporting?.jql?.[args.templateKey];
  const tmpl = String(raw || '').trim();
  if (!tmpl) return null;

  const priorityField = priorityJqlField(cfg?.reporting?.customFields?.priority);
  const slaField = priorityJqlField(cfg?.reporting?.customFields?.slaTimeToResolution);

  const expanded = args.projectKeys && args.projectKeys.length > 0
    ? expandProjectKeysInJql(tmpl, args.projectKeys)
    : expandProjectKeyInJql(tmpl, String(args.projectKey || '').trim());

  const withoutOrderBy = args.stripOrderBy ? stripOrderBy(expanded) : expanded;
  return rewriteTimeToResolutionFieldInJql(rewritePriorityFieldInJql(withoutOrderBy, priorityField), slaField).trim();
}

export function jiraOrJql(list: Array<string | null | undefined>): string | null {
  const parts = (list || []).map((s) => stripOrderBy(String(s || '').trim())).filter(Boolean);
  if (parts.length === 0) return null;
  if (parts.length === 1) return parts[0];
  return parts.map((p) => `(${p})`).join(' OR ');
}

export function openInNewTab(url: string | null | undefined): void {
  const u = String(url || '').trim();
  if (!u) return;
  window.open(u, '_blank', 'noopener,noreferrer');
}

